import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout, PageHeader } from "@/components/Layout";
import { MustangCard } from "@/components/MustangCard";
import { useFavorites } from "@/context/FavoritesContext";
import { Search, Heart, SlidersHorizontal, X, Award } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { AuctionCountdown } from "@/components/AuctionCountdown";
import { YearHighlights } from "@/components/YearHighlights";
import { YearTabs } from "@/components/YearTabs";

const DIV_ORDER = { "Youth In-Hand": 0, "Adult In-Hand": 1, "Adult Undersaddle": 2 };

export default function Mustangs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [horses, setHorses] = useState([]);
  const [filters, setFilters] = useState({});
  const [q, setQ] = useState("");
  const [active, setActive] = useState(() => (searchParams.get("auction") ? { auction_status: "In Auction" } : {}));
  const [favOnly, setFavOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [year, setYear] = useState(() => Number(searchParams.get("year")) || null);
  const [years, setYears] = useState([]);
  const { favorites } = useFavorites();

  useEffect(() => {
    api.get("/horses/filters").then((r) => setFilters(r.data)).catch(() => {});
    api.get("/horses/years").then((r) => setYears(r.data.years || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const params = { ...active };
    if (q) params.q = q;
    if (year) { params.year = year; delete params.auction_status; }
    api.get("/horses", { params }).then((r) => setHorses(r.data)).catch(() => {});
  }, [active, q, year]);

  const shown = useMemo(() => {
    const list = favOnly ? horses.filter((h) => favorites.includes(h.id)) : horses;
    if (!year) return list;
    return [...list].sort((a, b) => (DIV_ORDER[a.division] ?? 9) - (DIV_ORDER[b.division] ?? 9)
      || (a.archive_snapshot?.placing ?? 999) - (b.archive_snapshot?.placing ?? 999) || a.hip_number - b.hip_number);
  }, [horses, favOnly, favorites, year]);

  const setFilter = (key, val) =>
    setActive((a) => (a[key] === val ? (() => { const c = { ...a }; delete c[key]; return c; })() : { ...a, [key]: val }));

  const chip = (key, label) => (
    <button key={`${key}-${label}`} data-testid={`filter-${key}-${label}`} onClick={() => setFilter(key, label)}
      className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${active[key] === label ? "border-secondary bg-secondary text-white" : "border-border bg-card text-muted-foreground"}`}>
      {label}
    </button>
  );

  const hasFilters = Object.keys(active).length > 0;

  return (
    <AppLayout>
      <PageHeader subtitle={year ? `${horses.length} Mustang & Trainer Teams of ${year}` : `${(filters.numbers || []).length || horses.length} Mustang & Trainer Teams`} title={year ? `The ${year} Mustangs` : "The Mustangs"}
        action={
          <button data-testid="fav-filter-btn" onClick={() => setFavOnly((v) => !v)}
            className={`grid h-11 w-11 place-items-center rounded-full border transition-colors ${favOnly ? "border-secondary bg-secondary text-white" : "border-border bg-card text-muted-foreground"}`}>
            <Heart size={18} fill={favOnly ? "white" : "none"} />
          </button>
        } />

      {years.length > 0 && <YearTabs testid="mustang-year-tabs" years={years} year={year} setYear={(y) => { setYear(y); setSearchParams(y ? { year: String(y) } : {}, { replace: true }); }} />}
      {year ? <YearHighlights year={year} /> : <AuctionCountdown />}

      <div className="px-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="mustang-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search horse or trainer…"
            className="h-12 w-full rounded-xl border border-border bg-card pl-10 pr-4 text-base outline-none focus:ring-2 focus:ring-secondary" />
        </div>

        {/* Primary quick filters */}
        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {["Adult Undersaddle", "Adult In-Hand", "Youth In-Hand"].map((d) => (
            <button key={d} data-testid={`filter-primary-${d.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              onClick={() => setFilter("division", d)}
              className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${active.division === d ? "border-secondary bg-secondary text-white" : "border-border bg-card text-muted-foreground"}`}>{d}</button>
          ))}
          {!year && <button data-testid="filter-primary-auction" onClick={() => setFilter("auction_status", "In Auction")}
            className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${active.auction_status === "In Auction" ? "border-secondary bg-secondary text-white" : "border-border bg-card text-muted-foreground"}`}>Auction Horses</button>}
          <button data-testid="filter-primary-favorites" onClick={() => setFavOnly((v) => !v)}
            className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${favOnly ? "border-secondary bg-secondary text-white" : "border-border bg-card text-muted-foreground"}`}>Favorites</button>
          <button data-testid="toggle-more-filters" onClick={() => setShowFilters((v) => !v)}
            className="flex items-center gap-1 whitespace-nowrap rounded-full border border-border bg-card px-3.5 py-1.5 text-sm font-semibold text-primary">
            <SlidersHorizontal size={14} /> More
          </button>
          {hasFilters && (
            <button data-testid="clear-filters" onClick={() => setActive({})}
              className="flex items-center gap-1 whitespace-nowrap rounded-full border border-border bg-card px-3 py-1.5 text-sm font-semibold text-destructive">
              <X size={14} /> Clear
            </button>
          )}
          <span className="w-3 shrink-0" aria-hidden />
        </div>

        {showFilters && (
          <div className="mt-3 space-y-3 rounded-xl border border-border bg-card p-3">
            {[["sex", "Sex", filters.sexes], ["trainer_state", "State", filters.states],
              ["hma", "HMA", filters.hmas]].map(([key, label, opts]) => (
              <div key={key}>
                <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
                <div className="flex flex-wrap gap-2">{(opts || []).map((o) => chip(key, o))}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="px-4 pt-4 text-sm text-muted-foreground" data-testid="mustang-count">{shown.length} mustang{shown.length !== 1 ? "s" : ""}</p>
      {!year && <Link to="/fan-favorite" data-testid="directory-fanfav-banner"
        className="mx-4 mt-2 flex items-center gap-3 rounded-xl border border-secondary/40 bg-secondary/5 p-3">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-secondary text-white"><Award size={18} /></span>
        <div className="flex-1"><p className="text-sm font-bold text-primary">Fan Favorite Vote</p>
          <p className="text-xs text-muted-foreground">Vote in Adult Undersaddle, Adult In-Hand & Youth In-Hand</p></div>
        <span className="text-secondary">→</span>
      </Link>}
      <div className="grid grid-cols-2 gap-3 px-4 pt-2">
        {shown.map((h, i) => <MustangCard key={h.id} horse={h} index={i} />)}
      </div>
      {shown.length === 0 && <p className="px-4 pt-8 text-center text-muted-foreground">No mustangs match your filters.</p>}
    </AppLayout>
  );
}
